/**
 * ReferralShare Component
 *
 * Displays the user's referral code and sharing options.
 * Shows stats about successful referrals and tokens earned.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { Users, Copy, Check, Share2, Gift } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { useReferralCode } from '../../hooks/useReferral';
import sharedConstants from '../../constants/shared_constants.json';
import { publicAsset } from '../../utils/assetUrl';
import { TIMEOUTS } from '../../constants/timings';

interface ReferralShareProps {
  /** Optional className for styling */
  className?: string;
  /** Compact mode - shows less detail */
  compact?: boolean;
}

/**
 * ReferralShare - Component for sharing referral links
 *
 * Features:
 * - Displays unique referral code and URL
 * - Copy to clipboard functionality
 * - Native share API support (mobile)
 * - Shows referral stats (friends invited, tokens earned)
 */
export function ReferralShare({ className = '', compact = false }: ReferralShareProps) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useReferralCode();
  const [copied, setCopied] = useState(false);
  const copyResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup copy-reset timer on unmount to prevent state updates after unmount
  useEffect(() => {
    return () => {
      if (copyResetTimerRef.current) {
        clearTimeout(copyResetTimerRef.current);
      }
    };
  }, []);

  const copyToClipboard = useCallback(async () => {
    if (!data?.referral_url) return;

    try {
      await navigator.clipboard.writeText(data.referral_url);
      setCopied(true);
      if (copyResetTimerRef.current) {
        clearTimeout(copyResetTimerRef.current);
      }
      copyResetTimerRef.current = setTimeout(() => {
        setCopied(false);
        copyResetTimerRef.current = null;
      }, TIMEOUTS.COPY_FEEDBACK);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  }, [data?.referral_url]);

  const shareNative = useCallback(async () => {
    if (!data?.referral_url || !navigator.share) return;

    try {
      await navigator.share({
        title: t('referral.shareTitle', 'Invite to Asgard Studio'),
        text: t(
          'referral.shareText',
          'Create amazing videos with AI! Use my link and get 722 free tokens:'
        ),
        url: data.referral_url,
      });
    } catch {
      // User cancelled or share failed - ignore
    }
  }, [data?.referral_url, t]);

  // Loading state
  if (isLoading) {
    return (
      <div
        className={`animate-pulse rounded-xl bg-gray-200 dark:bg-gray-700 ${compact ? 'h-24' : 'h-32'} ${className}`}
      />
    );
  }

  // Error state
  if (error || !data) {
    return null;
  }

  // Compact version - informative CTA with copy invite link button
  if (compact) {
    const rewardTokens = sharedConstants.constants.misc.REFERRAL_REWARD_TOKENS;
    return (
      <div className={`rounded-lg p-3 ${className}`}>
        <div className="flex items-center gap-2 mb-2">
          <Gift className="h-4 w-4 text-purple-400 flex-shrink-0" />
          <span className="text-xs text-secondary-300">
            {t('referral.compactCta', {
              tokens: rewardTokens,
              defaultValue: 'Invite a friend and you both get {{tokens}} tokens!',
            })}
          </span>
        </div>
        <button
          onClick={copyToClipboard}
          className="w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-md bg-purple-600/40 hover:bg-purple-600/60 border border-purple-500/30 transition-colors text-xs font-medium text-purple-200 hover:text-white"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5 text-green-400" />
              {t('referral.copied', 'Copied!')}
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" />
              {t('referral.copyInviteLink', { defaultValue: 'Copy invite link' })}
            </>
          )}
        </button>
      </div>
    );
  }

  // Full version
  return (
    <div
      className={`bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl p-4 sm:p-6 text-white ${className}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <Users className="h-7 w-7 sm:h-8 sm:w-8" />
        <div>
          <h3 className="text-base sm:text-lg font-bold">
            {t('referral.title', 'Invite Friends')}
          </h3>
          <p className="text-xs sm:text-sm text-primary-100">
            {t('referral.subtitle', 'You and your friend both get 722 tokens!')}
          </p>
        </div>
      </div>

      {/* Referral Link */}
      <div className="bg-white/10 rounded-lg p-3 mb-4">
        <p className="text-xs text-primary-200 mb-1">
          {t('referral.yourLink', 'Your invite link:')}
        </p>
        <div className="flex items-center gap-2">
          <code className="flex-1 text-xs sm:text-sm truncate bg-white/5 px-2 py-1 rounded">
            {data.referral_url}
          </code>
          <button
            onClick={copyToClipboard}
            className="p-2 hover:bg-white/10 rounded transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            title={t('referral.copyLink', 'Copy link')}
          >
            {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
          </button>
          {typeof navigator.share === 'function' && (
            <button
              onClick={shareNative}
              className="p-2 hover:bg-white/10 rounded transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              title={t('referral.share', 'Share')}
            >
              <Share2 className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center justify-between text-sm">
        <div>
          <span className="text-primary-200">
            {t('referral.friendsInvited', 'Friends invited:')}
          </span>
          <span className="font-bold ml-2">{data.total_referrals}</span>
        </div>
        <div>
          <span className="text-primary-200">{t('referral.tokensEarned', 'Tokens earned:')}</span>
          <span className="font-bold ml-2 inline-flex items-center gap-1">
            {data.total_tokens_earned.toLocaleString()}{' '}
            <img
              src={publicAsset('token-coin-32.png')}
              alt=""
              className="h-3.5 w-3.5"
              draggable={false}
            />
          </span>
        </div>
      </div>

      {/* Copied feedback */}
      {copied && (
        <div className="mt-3 text-center text-sm text-primary-100 animate-pulse">
          {t('referral.copied', 'Copied!')}
        </div>
      )}
    </div>
  );
}

export default ReferralShare;
